/**
 * Real-time print receiver (mirror of the backend `/print` gateway).
 *
 * When a workstation prints to a printer that lives on another PC, the backend
 * pushes a `print:job` event to the dashboard(s) open on that PC instead of
 * relying on the print agent polling a queue. This module:
 *
 *   1. connects to the backend with the HttpOnly auth cookie,
 *   2. registers this workstation's identities (agent hostname + IPv4s) so it
 *      receives jobs targeted at it,
 *   3. prints each received job through the LOCAL print agent, and
 *   4. reports the result back.
 *
 * Only one browser tab prints each job (Web Locks), so several open tabs never
 * duplicate a receipt.
 */
import { toast } from 'sonner';
import { io, type Socket } from 'socket.io-client';
import { ENV } from '../config/env';
import { agentPrintDocument, agentRawPrint, getPrintAgentIdentities, probePrintAgent } from './printAgent';

interface PrintJob {
  id: string;
  kind: string;
  format?: string;
  printerLabel?: string;
  shareName: string;
  payload: string;
}

let socket: Socket | null = null;
let started = false;
let registerTimer: ReturnType<typeof setInterval> | null = null;
let lastRegisteredKey = '';
let agentRejectionWarned = false;

async function registerIdentities(): Promise<void> {
  if (!socket?.connected) return;
  const { hostname, ipAddresses } = await getPrintAgentIdentities();
  const identities = Array.from(new Set([hostname, ...ipAddresses].filter(Boolean)));
  if (identities.length === 0) {
    // No identities means the local agent could not be reached. If it answered
    // but rejected us (wrong origin / missing token), say so once: without this
    // the PC silently never receives the remote print jobs targeted at it.
    if (!agentRejectionWarned) {
      try {
        await probePrintAgent();
      } catch (err: any) {
        agentRejectionWarned = true;
        toast.error(
          `The print agent on this PC rejected the dashboard (${
            err?.message || 'unknown reason'
          }). Remote prints will not be received here.`,
        );
      }
    }
    return;
  }
  const key = identities.slice().sort().join(',');
  if (key === lastRegisteredKey) return;
  socket.emit('register', { identities });
  lastRegisteredKey = key;
}

async function printJobOnThisPc(job: PrintJob): Promise<void> {
  try {
    const format = (job.format || (job.kind === 'document' ? 'pdf' : 'raw')).toLowerCase();
    if (job.kind === 'document') {
      await agentPrintDocument(job.payload, format as 'raw' | 'pdf' | 'image', job.shareName, undefined);
    } else {
      await agentRawPrint(job.payload, job.shareName, undefined);
    }
    socket?.emit('print:result', { id: job.id, success: true });
    toast.success(`Printed ${job.printerLabel || 'job'} on ${job.shareName}`);
  } catch (err: any) {
    socket?.emit('print:result', { id: job.id, success: false, error: err?.message });
    toast.error(err?.message || 'Failed to print on this PC');
  }
}

async function handleJob(job: PrintJob): Promise<void> {
  if (!job?.id) return;
  const locks = (navigator as any)?.locks;
  if (locks?.request) {
    // Only the tab that grabs the lock for this job id prints it.
    await locks.request(`firstaid-print-${job.id}`, { ifAvailable: true }, async (lock: unknown) => {
      if (!lock) return;
      await printJobOnThisPc(job);
    });
    return;
  }
  await printJobOnThisPc(job);
}

/** Start listening for remote print jobs. Safe to call repeatedly. */
export function startPrintSocket(): void {
  if (started) return;
  started = true;

  socket = io(`${ENV.VITE_API_URL}/print`, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
    path: '/socket.io',
  });

  socket.on('connect', () => {
    lastRegisteredKey = '';
    void registerIdentities();
  });
  socket.on('print:job', (job: PrintJob) => {
    void handleJob(job);
  });
  socket.on('connect_error', () => {
    /* backend unreachable — realtime print unavailable, callers warn instead */
  });

  // Re-announce periodically so a workstation that boots its agent after login
  // (or changes IP) starts receiving jobs without a reload.
  registerTimer = setInterval(() => void registerIdentities(), 30_000);
}

/** Stop listening (logout / layout unmount). */
export function stopPrintSocket(): void {
  if (registerTimer) {
    clearInterval(registerTimer);
    registerTimer = null;
  }
  socket?.disconnect();
  socket = null;
  started = false;
  lastRegisteredKey = '';
  agentRejectionWarned = false;
}

/**
 * Ask the backend to print via the real-time socket (preferred over the legacy
 * REST relay): the backend resolves the owning agent, creates the job, and
 * pushes an identification-only `print:wakeup`. Returns `null` when the socket
 * is not connected, so the caller can fall back to the REST path.
 */
export function emitPrintRequest(input: {
  shareName: string;
  host?: string;
  kind?: 'raw' | 'document';
  format?: string;
  printerLabel?: string;
  source?: string;
  jobRef?: string;
  payload?: string;
}): Promise<{ ok: boolean; jobId?: string; delivered?: boolean; error?: string } | null> {
  if (!socket?.connected) return Promise.resolve(null);
  const active = socket;
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), 8000);
    active.emit('print:request', input, (res: any) => {
      clearTimeout(timer);
      resolve(res ?? { ok: false, error: 'no response' });
    });
  });
}
